import { cache } from '@/container/apollo-client/cache';
import {
  CreateOrUpdateDraftMessageMutationVariables,
  RecentMessageConversation,
  useCreateOrUpdateDraftMessageMutation,
} from '@/types/generated/graphql';
import { gql } from '@apollo/client';
import { MMSType } from '@modules/conversations/conversation.types';
import { useMessageScheduleStore } from '@modules/conversations/features/message-schedule/hooks/use-message-schedule-store';
import { MessageModeEnum } from '@modules/conversations/features/message-schedule/message-schedule.types';
import { custom_events } from '@shared/constants';
import { useEffect } from 'react';

export function useDraftMessageMutation() {
  const [draftMessage] = useCreateOrUpdateDraftMessageMutation();
  function draftMessageHandler(input: CreateOrUpdateDraftMessageMutationVariables) {
    const messageMode = useMessageScheduleStore.getState().messageMode;
    if (messageMode === MessageModeEnum.UPDATE) return;
    draftMessage({
      variables: input,
    });
  }

  function cacheUpdateRecentMessage({
    clientId,
    channelId,
    messageContent,
    mediaCount,
  }: {
    clientId: string;
    channelId: string;
    messageContent: string;
    mediaCount: number;
  }) {
    const FRAGMENT_RECENT_MESSAGE = gql`
      fragment AppRecentMessage on RecentMessageConversation {
        draft {
          mediaCount
          messageContent
        }
      }
    `;

    try {
      cache.writeFragment<RecentMessageConversation>({
        id: `RecentMessageConversation:{"client":{"id":${clientId}},"channel":{"id":${channelId}}}`,
        fragment: FRAGMENT_RECENT_MESSAGE,
        data: {
          draft: {
            mediaCount,
            messageContent,
          },
        },
      });
    } catch {
      console.error('There was some issue on cacheUpdateRecentMessage');
    }
  }

  useEffect(() => {
    function draftHandler(e: Event) {
      const messageMode = useMessageScheduleStore.getState().messageMode;
      if (messageMode === MessageModeEnum.UPDATE) return;
      const customEvent = e as CustomEvent<
        CreateOrUpdateDraftMessageMutationVariables['input'] & { medias: MMSType[] }
      >;
      const clientId = customEvent.detail?.clientId ?? 0;
      const channelId = customEvent.detail?.channelId ?? 0;
      const messageContent = customEvent.detail?.messageContent + '';
      const medias = customEvent.detail?.medias ?? [];
      //filter error files
      const mediaIds = medias
        .filter((file) => file.status === 'done')
        .map((file) => Number(file.id ?? file.uid))
        .filter((id) => !isNaN(id));

      cacheUpdateRecentMessage({
        clientId: clientId + '',
        channelId: channelId + '',
        messageContent,
        mediaCount: mediaIds.length,
      });

      draftMessageHandler({
        input: {
          clientId: +clientId,
          channelId: +channelId,
          messageContent,
          mediaIds,
        },
      });
    }

    window.addEventListener(custom_events.save_as_draft, draftHandler);

    return () => {
      window.removeEventListener(custom_events.save_as_draft, draftHandler);
    };
  }, []);
}
